import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

const SummaryContainer = styled(Paper)(({ theme }) => ({
  background: '#1a1a2e',
  borderRadius: '24px',
  padding: theme.spacing(4),
  border: '1px solid rgba(102, 126, 234, 0.1)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: '12px !important',
  marginBottom: theme.spacing(2),
  border: '1px solid rgba(102, 126, 234, 0.1)',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: theme.spacing(2, 0),
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: '#1a1a2e',
  borderRadius: '12px',
  '&.Mui-expanded': {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
}));

const KeyPointBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
  borderRadius: '12px',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: '1px solid rgba(102, 126, 234, 0.2)',
}));

const ImportantChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(102, 126, 234, 0.2)',
  color: '#e2e8f0',
  fontWeight: 600,
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

export default function SummaryView({ content }) {
  const [summaryData, setSummaryData] = useState([]);

  useEffect(() => {
    // Parse content to extract key points and create summary
    const lines = content.split('\n');
    const sections = [];
    let currentSection = null;
    let currentSubsection = null;

    lines.forEach((line) => {
      // Main headers (h2 or h3 if no h2 found)
      const h2Match = line.match(/^##\s+(.+)$/);
      const h3Match = line.match(/^###\s+(.+)$/);
      const h4Match = line.match(/^####\s+(.+)$/);
      
      // For h3-only documents (like public resumos), treat h3 as main sections
      const hasH2InDocument = lines.some(l => l.match(/^##\s+/));
      
      if (h2Match || (!hasH2InDocument && h3Match)) {
        if (currentSection) {
          sections.push(currentSection);
        }
        const headerMatch = h2Match || h3Match;
        currentSection = {
          title: headerMatch[1].replace(/\*\*/g, '').trim(),
          subsections: [],
          keyPoints: [],
        };
        currentSubsection = null;
        return;
      }

      // Subheaders (h3 when h2 exists, or h4)
      if ((hasH2InDocument && h3Match && currentSection) || (h4Match && currentSection)) {
        if (currentSubsection) {
          currentSection.subsections.push(currentSubsection);
        }
        const subMatch = h3Match || h4Match;
        currentSubsection = {
          title: subMatch[1].replace(/\*\*/g, '').trim(),
          points: [],
        };
        return;
      }

      // Extract important points (bullets with bold text)
      const bulletMatch = line.match(/^\s*[\*\-]\s+\*\*([^:]+):\*\*\s*(.+)$/);
      const simpleBulletMatch = line.match(/^\s*[\*\-]\s+(.+)$/);
      
      if (bulletMatch) {
        const point = {
          concept: bulletMatch[1].trim(),
          description: bulletMatch[2].trim(),
        };
        
        if (currentSubsection) {
          currentSubsection.points.push(point);
        } else if (currentSection) {
          currentSection.keyPoints.push(point);
        }
      } else if (simpleBulletMatch && currentSection) {
        // Handle simple bullet points
        const bulletText = simpleBulletMatch[1].trim();
        const colonMatch = bulletText.match(/^([^:]+):\s*(.+)$/);
        
        if (colonMatch) {
          const point = {
            concept: colonMatch[1].replace(/\*\*/g, '').trim(),
            description: colonMatch[2].replace(/\*\*/g, '').trim(),
          };
          if (currentSubsection) {
            currentSubsection.points.push(point);
          } else if (currentSection) {
            currentSection.keyPoints.push(point);
          }
        }
      }

      // Extract emphasized text as key points
      const emphasisMatch = line.match(/\*\*([^*]+)\*\*/g);
      if (emphasisMatch && currentSection && !bulletMatch && !simpleBulletMatch) {
        emphasisMatch.forEach(match => {
          const text = match.replace(/\*\*/g, '').trim();
          if (text.length > 10 && text.length < 100) {
            currentSection.keyPoints.push({
              concept: text,
              description: '',
              isHighlight: true,
            });
          }
        });
      }
      
      // Extract regular paragraphs as content points if they're not empty
      if (!h2Match && !h3Match && !h4Match && !bulletMatch && !simpleBulletMatch && line.trim().length > 20 && currentSection) {
        // Check if it's a meaningful paragraph
        const trimmedLine = line.trim();
        if (!trimmedLine.startsWith('---') && !trimmedLine.match(/^#+\s/)) {
          // Extract key concept from the paragraph if possible
          const boldMatch = trimmedLine.match(/\*\*([^*]+)\*\*/);
          const conceptText = boldMatch ? boldMatch[1] : 'Descrição';
          
          currentSection.keyPoints.push({
            concept: conceptText,
            description: trimmedLine.replace(/\*\*/g, ''),
            isParagraph: true,
          });
        }
      }
    });

    if (currentSubsection && currentSection) {
      currentSection.subsections.push(currentSubsection);
    }
    if (currentSection) {
      sections.push(currentSection);
    }

    setSummaryData(sections);
  }, [content]);

  return (
    <SummaryContainer elevation={0}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <LightbulbIcon sx={{ fontSize: 48, color: '#667eea', mb: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#f1f5f9', mb: 1 }}>
          Resumo do Conteúdo
        </Typography>
        <Typography variant="body1" sx={{ color: '#cbd5e1' }}>
          Principais conceitos e pontos importantes organizados para revisão rápida
        </Typography>
      </Box>

      {summaryData.map((section, index) => (
        <StyledAccordion key={index} defaultExpanded={index === 0}>
          <StyledAccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel${index}-content`}
            id={`panel${index}-header`}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#e2e8f0' }}>
                {section.title}
              </Typography>
              <Chip 
                label={`${section.keyPoints.length + section.subsections.reduce((acc, sub) => acc + sub.points.length, 0)} pontos`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </StyledAccordionSummary>
          <AccordionDetails>
            {/* Key Points at section level */}
            {section.keyPoints.length > 0 && (
              <KeyPointBox sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#667eea' }}>
                  <StarIcon sx={{ fontSize: 18, verticalAlign: 'text-bottom', mr: 0.5 }} />
                  Pontos Principais
                </Typography>
                <List dense>
                  {section.keyPoints.map((point, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box>
                            {point.isHighlight && (
                              <ImportantChip
                                label="Importante"
                                size="small"
                                icon={<StarIcon />}
                              />
                            )}
                            {point.isParagraph ? (
                              <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                                {point.description}
                              </Typography>
                            ) : (
                              <>
                                <Typography variant="body2" component="span" sx={{ fontWeight: 600, color: '#f3f4f6' }}>
                                  {point.concept}
                                </Typography>
                                {point.description && (
                                  <Typography variant="body2" component="span" sx={{ ml: 1, color: '#e2e8f0' }}>
                                    {point.concept !== point.description ? `- ${point.description}` : ''}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </KeyPointBox>
            )}

            {/* Subsections */}
            {section.subsections.map((subsection, subIdx) => (
              <Box key={subIdx} sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#e2e8f0' }}>
                  {subsection.title}
                </Typography>
                <List dense sx={{ pl: 2 }}>
                  {subsection.points.map((point, pointIdx) => (
                    <ListItem key={pointIdx}>
                      <ListItemIcon>
                        <CheckCircleIcon color="action" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <strong>{point.concept}:</strong> {point.description}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </AccordionDetails>
        </StyledAccordion>
      ))}

      {summaryData.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" sx={{ color: '#cbd5e1' }}>
            Gerando resumo do conteúdo...
          </Typography>
        </Box>
      )}
    </SummaryContainer>
  );
}